/**
 * Studio Booking · Bookings table + detail dialog + child dialogs — extracted Oct 2026.
 * Contents:
 *   • Bookings                 — main admin table (filters, bulk, export)
 *   • BookingCommentsDialog    — comments-only side drawer
 *   • BookingDetailDialog      — full booking detail + ledger
 *   • BookingSection/Field/QuoteRow — layout primitives (internal)
 *   • UpiVerifyDialog          — admin "✓ Verify UPI" flow
 *   • ReleaseDepositDialog     — releases held damage deposit back
 *   • AssignCrewDialog         — crew assignment per booking
 *   • LinkClientProjectDialog  — link booking → client + project
 *   • RescheduleAcceptFooter   — one-tap accept/decline in comment thread
 *   • BookingPaymentsLedger    — read-only payment timeline
 *   • RecordPaymentDialog      — admin manual payment entry
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import AdvancedTable from "@/components/table/AdvancedTable";
import CommentThread from "@/components/CommentThread";
import {
  Trash2, Trash, X as XIcon, Plus, Edit, Mail, MessageCircle, MessageSquare,
  Phone, Save, Send, Calendar, CalendarDays, Receipt, Link as LinkIcon,
  Image as ImageIcon, IndianRupee, RefreshCw,
} from "lucide-react";
import { MediaBadge } from "./MediaUpload";
import { ManualBookingDialog } from "./ManualBooking";
import { statusColor } from "./common";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";

// CSV/XLSX/PDF export, grand-totals — plus a row of quick-filter pill
// chips ("Upcoming", "Today", "Pending payment", …) that persist
// alongside the table layout state via extraLayoutState.
const QUICK_PILLS = [
  { k: "all",             label: "All",                 tone: "muted" },
  { k: "upcoming",        label: "Upcoming",            tone: "primary" },
  { k: "today",           label: "Today",               tone: "primary" },
  { k: "this_week",       label: "This week",           tone: "primary" },
  { k: "pending_payment", label: "Pending payment",     tone: "amber" },
  { k: "awaiting_upi",    label: "Awaiting UPI verify", tone: "emerald" },
  { k: "cancelled",       label: "Cancelled",           tone: "rose" },
  { k: "off_hours",       label: "Off-hours only",      tone: "amber" },
  { k: "deposit_release", label: "Deposit to release",  tone: "primary" },
];


export function Bookings({ ws, canManage }) {
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [depBooking, setDepBooking] = useState(null);
  const [assignBooking, setAssignBooking] = useState(null);
  const [linkBooking, setLinkBooking] = useState(null);
  const [upiBooking, setUpiBooking] = useState(null);
  const [commentBooking, setCommentBooking] = useState(null); // Feb 2026 · comments drawer
  const [detailBooking, setDetailBooking] = useState(null);   // Feb 2026 · Edit-Booking popup
  const [manualOpen, setManualOpen] = useState(false);
  const [pill, setPill] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get(`/workspaces/${ws}/studio/bookings`);
      setRows(data.bookings || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  const loadRefs = async () => {
    try {
      const [mRes, cRes, pRes, sRes] = await Promise.all([
        api.get(`/workspaces/${ws}/members`),
        api.get(`/workspaces/${ws}/clients`),
        api.get(`/workspaces/${ws}/projects/picker`),
        api.get(`/workspaces/${ws}/studio/spaces`),
      ]);
      setMembers(Array.isArray(mRes.data) ? mRes.data : []);
      setClients(Array.isArray(cRes.data) ? cRes.data : []);
      setProjects(Array.isArray(pRes.data) ? pRes.data : []);
      setSpaces(sRes.data?.spaces || []);
    } catch (_e) { /* silent — fallback UIs show empty */ }
  };
  useEffect(() => { if (ws) { load(); loadRefs(); } }, [ws]);

  const cancel = async (bid) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      const { data } = await api.post(`/workspaces/${ws}/studio/bookings/${bid}/cancel`, { reason: "Admin cancellation" });
      toast.success(`Cancelled · refund ₹${data.refund_amount} (${data.refund_pct}%)`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Cancel failed")); }
  };

  const spaceName = (sid) => spaces.find((s) => s.id === sid)?.name || (sid ? sid.slice(0, 8) : "—");
  const memberLabel = (uid) => {
    const m = members.find((x) => (x.user_id || x.id) === uid);
    return m?.name || m?.email || uid.slice(0, 6);
  };
  const clientName = (cid) => clients.find((c) => c.id === cid)?.name || "";
  const projectName = (pid) => projects.find((p) => p.id === pid)?.name || "";

  const cleanPhone = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/\D+/g, "");
    if (!digits) return "";
    return digits.length === 10 ? `91${digits}` : digits;
  };
  const waLink = (r) => {
    const d = cleanPhone(r.customer_phone);
    if (!d) return "";
    const when = (r.slot_start || "").replace("T", " ").slice(0, 16);
    const msg = `Hi ${r.customer_name || "there"}, this is regarding your booking ${r.code || ""}${r.ref_no ? ` (#${r.ref_no})` : ""}${when ? ` on ${when}` : ""}.`;
    return `https://wa.me/${d}?text=${encodeURIComponent(msg)}`;
  };
  const durationLabel = (start, end) => {
    if (!start || !end) return "";
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (!(ms > 0)) return "";
    const h = Math.floor(ms / 3600000);
    const m = Math.round((ms % 3600000) / 60000);
    return `${h}h${m ? ` ${m}m` : ""}`;
  };
  const sourceLabel = (s) => {
    switch ((s || "").toLowerCase()) {
      case "admin":  return "Admin";
      case "manual": return "Manual";
      case "public": return "Public widget";
      default:       return s || "Public widget";
    }
  };
  const paymentMethod = (r) => {
    if (r.upi_proof || r.txn_id || r.status === "pending_verification") return "upi_qr";
    if (r.source === "manual" || r.source === "admin") return "manual";
    return "cashfree";
  };
  const paymentLabel = (m) => ({
    cashfree: "Cashfree", upi_qr: "UPI QR", manual: "Manual",
  }[m] || m);

  // Quick-filter pill logic. All time comparisons use local-day
  // boundaries so "Today" matches the admin's timezone, not UTC.
  const pillRows = useMemo(() => {
    if (pill === "all") return rows;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const nextDay = new Date(start.getTime() + 86400 * 1000);
    const weekEnd = new Date(start.getTime() + 7 * 86400 * 1000);
    const toIso = (d) => d.toISOString();
    const startIso = toIso(start);
    const nextDayIso = toIso(nextDay);
    const weekEndIso = toIso(weekEnd);
    return rows.filter((r) => {
      switch (pill) {
        case "upcoming":
          return (r.slot_start || "") >= startIso && ["pending", "confirmed"].includes(r.status);
        case "today":
          return (r.slot_start || "") >= startIso && (r.slot_start || "") < nextDayIso;
        case "this_week":
          return (r.slot_start || "") >= startIso && (r.slot_start || "") < weekEndIso;
        case "pending_payment":  return r.status === "pending";
        case "awaiting_upi":     return r.status === "pending_verification";
        case "cancelled":        return r.status === "cancelled";
        case "off_hours":        return !!r.is_off_hours;
        case "deposit_release":  return (r.quote?.damage_deposit || 0) > 0 && !r.deposit_released_at && r.status !== "cancelled";
        default: return true;
      }
    });
  }, [rows, pill]);

  const columns = useMemo(() => [
    {
      key: "code", label: "Code", width: 130, sortable: true,
      // Feb 2026 · R280.4 · Include ref_no + booking id in the
      // searchable accessor so pasting either an internal UUID or the
      // "#1042" reference number returns the row.
      accessor: (r) => `${r.code || ""} ${r.ref_no ? "#" + r.ref_no : ""} ${r.id || ""}`.trim(),
      exportValue: (r) => `${r.code || ""}${r.ref_no ? ` #${r.ref_no}` : ""}`,
      render: (r) => (
        <div className="font-mono text-xs leading-tight" data-testid={`sb-booking-code-${r.id}`}>
          <div>{r.code}</div>
          {r.ref_no && <div className="text-[10px] text-muted-foreground">#{r.ref_no}</div>}
        </div>
      ),
    },
    {
      key: "slot_start", label: "When", width: 170, sortable: true, filterType: "date-range",
      accessor: (r) => r.slot_start || "",
      exportValue: (r) => (r.slot_start || "").replace("T", " ").slice(0, 16),
      render: (r) => (
        <div className="text-xs leading-tight">
          <div>{r.slot_start?.replace("T", " ").slice(0, 16)}</div>
          <div className="text-[10px] text-muted-foreground">
            {durationLabel(r.slot_start, r.slot_end)}
            {r.is_off_hours && (
              <span className="ml-1 text-[9px] px-1 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 uppercase"
                    title={`Off-hours: ${(r.off_hours_reasons || []).join(", ")}`}>
                OFF-HRS
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "space", label: "Space", width: 150, sortable: true, groupable: true, filterType: "select",
      filterOptions: [
        { value: "__offsite__", label: "🚚 Offsite" },
        ...spaces.map((s) => ({ value: s.id, label: s.name })),
      ],
      accessor: (r) => r.is_offsite ? "__offsite__" : (r.space_id || ""),
      exportValue: (r) => r.is_offsite ? "Offsite" : spaceName(r.space_id),
      render: (r) => (
        <span className="text-xs">
          {r.is_offsite ? "🚚 Offsite" : spaceName(r.space_id)}
        </span>
      ),
    },
    {
      key: "customer", label: "Customer", width: 240, sortable: true, filterType: "text",
      accessor: (r) => (r.customer_name || "").toLowerCase(),
      exportValue: (r) => `${r.customer_name || ""} · ${r.customer_email || ""} · ${r.customer_phone || ""}`,
      render: (r) => {
        const wa = waLink(r);
        return (
          <div className="text-xs leading-tight">
            <div className="font-medium">{r.customer_name || "—"}</div>
            {r.customer_email && (
              <a href={`mailto:${r.customer_email}`} onClick={(e) => e.stopPropagation()}
                  className="text-[10px] text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                  data-testid={`sb-booking-email-${r.id}`}>
                <Mail className="w-2.5 h-2.5" />{r.customer_email}
              </a>
            )}
            {r.customer_phone && (
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <a href={`tel:${r.customer_phone}`} onClick={(e) => e.stopPropagation()}
                    className="text-[10px] text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                    data-testid={`sb-booking-phone-${r.id}`}>
                  <Phone className="w-2.5 h-2.5" />{r.customer_phone}
                </a>
                {wa && (
                  <a href={wa} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                      title={`Message ${r.customer_name || "customer"} on WhatsApp`}
                      className="text-[10px] text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-300 bg-emerald-50"
                      data-testid={`sb-booking-wa-${r.id}`}>
                    <MessageCircle className="w-2.5 h-2.5" />WA
                  </a>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "phone", label: "Phone", width: 140, sortable: true, filterType: "text",
      accessor: (r) => r.customer_phone || "",
      render: (r) => r.customer_phone
        ? <a href={`tel:${r.customer_phone}`} onClick={(e) => e.stopPropagation()} className="text-xs hover:text-primary">{r.customer_phone}</a>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "email", label: "Email", width: 210, sortable: true, filterType: "text",
      accessor: (r) => r.customer_email || "",
      render: (r) => r.customer_email
        ? <a href={`mailto:${r.customer_email}`} onClick={(e) => e.stopPropagation()} className="text-xs hover:text-primary">{r.customer_email}</a>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "total", label: "Total", width: 110, sortable: true, sum: true, align: "right", type: "currency",
      accessor: (r) => Number(r.quote?.total || 0),
      exportValue: (r) => Number(r.quote?.total || 0),
      render: (r) => <span className="font-mono text-xs">₹{(r.quote?.total || 0).toFixed(2)}</span>,
    },
    // Feb 2026 · R280.4 · Payment ledger columns — surfaced next to
    // Total so the front-desk sees at a glance whether an inbound
    // guest has already cleared their balance. Balance is coloured
    // red (outstanding), emerald (excess/credit) or muted (zero).
    {
      key: "paid", label: "Paid", width: 105, sortable: true, sum: true, align: "right", type: "currency",
      accessor: (r) => (r.payments || [])
        .filter((p) => (p.status || "success") === "success")
        .reduce((s, p) => s + Number(p.amount || 0), 0),
      exportValue: (r) => (r.payments || [])
        .filter((p) => (p.status || "success") === "success")
        .reduce((s, p) => s + Number(p.amount || 0), 0),
      render: (r) => {
        const paid = (r.payments || [])
          .filter((p) => (p.status || "success") === "success")
          .reduce((s, p) => s + Number(p.amount || 0), 0);
        return <span className="font-mono text-xs">{paid > 0 ? `₹${paid.toFixed(2)}` : "—"}</span>;
      },
    },
    {
      key: "balance", label: "Balance", width: 115, sortable: true, sum: true, align: "right", type: "currency",
      accessor: (r) => {
        const paid = (r.payments || [])
          .filter((p) => (p.status || "success") === "success")
          .reduce((s, p) => s + Number(p.amount || 0), 0);
        return Number(r.quote?.total || 0) - paid;
      },
      exportValue: (r) => {
        const paid = (r.payments || [])
          .filter((p) => (p.status || "success") === "success")
          .reduce((s, p) => s + Number(p.amount || 0), 0);
        return Number(r.quote?.total || 0) - paid;
      },
      render: (r) => {
        const paid = (r.payments || [])
          .filter((p) => (p.status || "success") === "success")
          .reduce((s, p) => s + Number(p.amount || 0), 0);
        const bal = Number(r.quote?.total || 0) - paid;
        if (r.status === "cancelled") return <span className="text-[10px] text-muted-foreground">—</span>;
        const tone = bal > 0
          ? "text-rose-700 font-semibold"
          : bal < 0
            ? "text-emerald-700 font-semibold"
            : "text-muted-foreground";
        return (
          <span className={`font-mono text-xs ${tone}`} data-testid={`sb-booking-balance-${r.id}`}
                title={bal < 0 ? "Customer paid in excess — credit on account" : bal > 0 ? "Payment still due" : "Fully settled"}>
            {bal === 0 ? "Paid" : bal < 0 ? `+₹${(-bal).toFixed(2)}` : `₹${bal.toFixed(2)}`}
          </span>
        );
      },
    },
    {
      key: "status", label: "Status", width: 140, sortable: true, groupable: true,
      filterType: "select",
      filterOptions: [
        { value: "pending", label: "Pending" },
        { value: "pending_verification", label: "Awaiting UPI verify" },
        { value: "confirmed", label: "Confirmed" },
        { value: "cancelled", label: "Cancelled" },
        { value: "completed", label: "Completed" },
      ],
      accessor: (r) => r.status || "",
      render: (r) => (
        <span className={`text-[10px] px-1.5 py-0.5 border ${statusColor(r.status)}`}
                data-testid={`sb-booking-status-${r.id}`}>
          {r.status}
        </span>
      ),
    },
    {
      key: "source", label: "Source", width: 120, sortable: true, groupable: true,
      filterType: "select",
      filterOptions: [
        { value: "public", label: "Public widget" },
        { value: "admin",  label: "Admin" },
        { value: "manual", label: "Manual" },
      ],
      accessor: (r) => (r.source || "public"),
      exportValue: (r) => sourceLabel(r.source),
      render: (r) => <span className="text-xs">{sourceLabel(r.source)}</span>,
    },
    {
      key: "created_at", label: "Booked on", width: 140, sortable: true, filterType: "date-range",
      accessor: (r) => r.created_at || "",
      exportValue: (r) => (r.created_at || "").slice(0, 10),
      render: (r) => (
        <div className="text-xs leading-tight">
          <div>{(r.created_at || "").slice(0, 10) || "—"}</div>
          <div className="text-[10px] text-muted-foreground">{(r.created_at || "").slice(11, 16)}</div>
        </div>
      ),
    },
    {
      key: "crew", label: "Crew", width: 170, filterType: "text",
      accessor: (r) => (r.assigned_user_ids || []).map(memberLabel).join(", "),
      render: (r) => {
        const ids = r.assigned_user_ids || [];
        return (
          <div className="flex flex-wrap gap-0.5 items-center" data-testid={`sb-booking-crew-${r.id}`}>
            {ids.length === 0
              ? <span className="text-[10px] text-muted-foreground italic">Unassigned</span>
              : ids.slice(0, 2).map((uid) => (
                  <span key={uid} className="text-[10px] bg-primary/10 text-primary px-1 py-0.5 border border-primary/30 whitespace-nowrap">
                    {memberLabel(uid)}
                  </span>
                ))}
            {ids.length > 2 && <span className="text-[10px] text-muted-foreground">+{ids.length - 2}</span>}
            {canManage && (
              <button onClick={(e) => { e.stopPropagation(); setAssignBooking(r); }}
                      className="text-[10px] text-primary hover:underline ml-1"
                      data-testid={`sb-booking-assign-${r.id}`}>
                {ids.length ? "edit" : "assign"}
              </button>
            )}
          </div>
        );
      },
    },
    {
      key: "deposit", label: "Deposit", width: 120, sortable: true, sum: true, align: "right",
      accessor: (r) => Number(r.quote?.damage_deposit || 0),
      exportValue: (r) => Number(r.quote?.damage_deposit || 0),
      render: (r) => {
        const dep = Number(r.quote?.damage_deposit || 0);
        if (dep <= 0) return <span className="text-muted-foreground">—</span>;
        if (r.deposit_released_at) return (
          <span className="text-emerald-700 font-mono text-xs"
                title={`Refunded ₹${(r.deposit_refund_amount || 0).toFixed(2)} on ${r.deposit_released_at?.slice(0, 10)}`}>
            ✓ ₹{(r.deposit_refund_amount || 0).toFixed(0)}
          </span>
        );
        return <span className="font-mono text-xs">₹{dep.toFixed(0)}</span>;
      },
    },
    {
      key: "client_project", label: "Client / Project", width: 200, filterType: "text",
      accessor: (r) => `${clientName(r.client_id)} ${projectName(r.project_id)}`.trim(),
      render: (r) => {
        const c = clientName(r.client_id), p = projectName(r.project_id);
        return (
          <div className="text-xs leading-tight">
            {c || p ? (
              <>
                {c && <div className="text-primary/80">{c}</div>}
                {p && <div className="text-muted-foreground">{p}</div>}
              </>
            ) : <span className="text-muted-foreground italic">Not linked</span>}
            {canManage && (
              <button onClick={(e) => { e.stopPropagation(); setLinkBooking(r); }}
                      className="text-[10px] text-primary hover:underline"
                      data-testid={`sb-booking-link-${r.id}`}>
                {(r.client_id || r.project_id) ? "edit link" : "link"}
              </button>
            )}
          </div>
        );
      },
    },
    {
      key: "promo", label: "Promo", width: 120, sortable: true, filterType: "text",
      accessor: (r) => r.promo_code || "",
      render: (r) => r.promo_code
        ? <span className="text-xs font-mono uppercase text-amber-700">{r.promo_code}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "payment_method", label: "Payment", width: 110, sortable: true, groupable: true,
      filterType: "select",
      filterOptions: [
        { value: "cashfree", label: "Cashfree" },
        { value: "upi_qr",   label: "UPI QR" },
        { value: "manual",   label: "Manual" },
      ],
      accessor: (r) => paymentMethod(r),
      exportValue: (r) => paymentLabel(paymentMethod(r)),
      render: (r) => <span className="text-xs">{paymentLabel(paymentMethod(r))}</span>,
    },
    {
      key: "txn_id", label: "Txn ID", width: 150, filterType: "text",
      accessor: (r) => r.txn_id || "",
      render: (r) => r.txn_id
        ? <span className="font-mono text-[10px]">{r.txn_id}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "notes", label: "Notes", width: 220, filterType: "text",
      accessor: (r) => r.notes || "",
      exportValue: (r) => r.notes || "",
      render: (r) => r.notes
        ? <span className="text-xs" title={r.notes}>{r.notes.length > 40 ? r.notes.slice(0, 40) + "…" : r.notes}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "off_hours", label: "Off-hrs", width: 90, groupable: true,
      filterType: "select",
      filterOptions: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }],
      accessor: (r) => r.is_off_hours ? "yes" : "no",
      render: (r) => r.is_off_hours
        ? <span className="text-amber-700 text-xs font-semibold">Yes</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "comments", label: "Comments", width: 110,
      accessor: (r) => Number(r.comment_count || 0),
      exportValue: (r) => Number(r.comment_count || 0),
      render: (r) => (
        <button onClick={(e) => { e.stopPropagation(); setCommentBooking(r); }}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                data-testid={`sb-booking-comments-${r.id}`}
                title="Open comments">
          <MessageSquare className="w-3 h-3" />
          {r.comment_count ? r.comment_count : "Add"}
        </button>
      ),
    },
    {
      key: "actions", label: "", width: 220, sortable: false,
      accessor: () => "",
      render: (r) => {
        const dep = Number(r.quote?.damage_deposit || 0);
        const released = !!r.deposit_released_at;
        return (
          <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
            {r.status === "pending_verification" && canManage && (
              <button onClick={() => setUpiBooking(r)}
                      className="text-emerald-700 text-[11px] hover:underline inline-flex items-center gap-1 font-semibold"
                      data-testid={`sb-booking-verify-upi-${r.id}`}>
                ✓ Verify UPI
              </button>
            )}
            {dep > 0 && !released && canManage && (
              <button onClick={() => setDepBooking(r)}
                      className="text-primary text-[11px] hover:underline inline-flex items-center gap-1"
                      data-testid={`sb-booking-release-${r.id}`}>
                <IndianRupee className="w-3 h-3" /> Release
              </button>
            )}
            {r.status !== "cancelled" && canManage && (
              <button onClick={() => cancel(r.id)}
                      className="text-rose-600 text-[11px] hover:underline"
                      data-testid={`sb-booking-cancel-${r.id}`}>
                Cancel
              </button>
            )}
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [spaces, members, clients, projects, canManage]);

  const pillCount = (key) => {
    if (key === "all") return rows.length;
    // Reuse pillRows logic against full rows for the given key.
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const nextDay = new Date(start.getTime() + 86400 * 1000);
    const weekEnd = new Date(start.getTime() + 7 * 86400 * 1000);
    const startIso = start.toISOString();
    const nextDayIso = nextDay.toISOString();
    const weekEndIso = weekEnd.toISOString();
    return rows.filter((r) => {
      switch (key) {
        case "upcoming":        return (r.slot_start || "") >= startIso && ["pending", "confirmed"].includes(r.status);
        case "today":           return (r.slot_start || "") >= startIso && (r.slot_start || "") < nextDayIso;
        case "this_week":       return (r.slot_start || "") >= startIso && (r.slot_start || "") < weekEndIso;
        case "pending_payment": return r.status === "pending";
        case "awaiting_upi":    return r.status === "pending_verification";
        case "cancelled":       return r.status === "cancelled";
        case "off_hours":       return !!r.is_off_hours;
        case "deposit_release": return (r.quote?.damage_deposit || 0) > 0 && !r.deposit_released_at && r.status !== "cancelled";
        default: return false;
      }
    }).length;
  };

  const pillClass = (p, active) => {
    const base = "h-7 px-2.5 text-[10px] uppercase tracking-widest font-semibold border transition-colors inline-flex items-center gap-1.5 rounded-none";
    if (active) return `${base} bg-primary text-primary-foreground border-primary`;
    if (p.tone === "amber")   return `${base} bg-amber-50   text-amber-800   border-amber-300 hover:bg-amber-100`;
    if (p.tone === "rose")    return `${base} bg-rose-50    text-rose-700    border-rose-300 hover:bg-rose-100`;
    if (p.tone === "emerald") return `${base} bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100`;
    if (p.tone === "primary") return `${base} bg-primary/5  text-primary     border-primary/30 hover:bg-primary/10`;
    return `${base} bg-muted text-muted-foreground border-border hover:bg-muted/70`;
  };

  return (
    <div data-testid="sb-bookings-list" className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex flex-wrap gap-1.5" data-testid="sb-bookings-pills">
          {QUICK_PILLS.map((p) => {
            const active = pill === p.k;
            const n = pillCount(p.k);
            return (
              <button key={p.k} onClick={() => setPill(p.k)}
                      className={pillClass(p, active)}
                      data-testid={`sb-bookings-pill-${p.k}`}>
                {p.label}
                <span className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-[9px] font-mono ${active ? "bg-white/25" : "bg-white/40 border border-current/20"}`}>
                  {n}
                </span>
              </button>
            );
          })}
        </div>
        {canManage && (
          <Button size="sm" className="rounded-none h-8" onClick={() => setManualOpen(true)}
                   data-testid="sb-booking-manual-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add manual booking
          </Button>
        )}
      </div>
      <ManualBookingDialog ws={ws} open={manualOpen} onClose={() => setManualOpen(false)} onDone={load} />

      <AdvancedTable
        tableKey="studio-bookings"
        title="Bookings"
        rows={pillRows}
        columns={columns}
        emptyIcon={Calendar}
        emptyTitle={rows.length === 0 ? "No bookings yet" : "No bookings match"}
        emptyDescription={rows.length === 0
          ? "Direct customers to your public booking widget or create one manually."
          : "Adjust filters or clear the active pill to see everything."}
        onRowClick={(r) => setDetailBooking(r)}
        extraLayoutState={{ pill }}
        onApplyExtraLayoutState={(s) => {
          if (s && typeof s.pill === "string") setPill(s.pill);
        }}
      />

      <ReleaseDepositDialog ws={ws} booking={depBooking} onClose={() => setDepBooking(null)} onDone={load} />
      <AssignCrewDialog ws={ws} booking={assignBooking} members={members}
                          onClose={() => setAssignBooking(null)} onDone={load} />
      <LinkClientProjectDialog ws={ws} booking={linkBooking} clients={clients}
                                 onClose={() => setLinkBooking(null)} onDone={load} />
      <UpiVerifyDialog ws={ws} booking={upiBooking}
                          onClose={() => setUpiBooking(null)} onDone={load} />
      <BookingCommentsDialog ws={ws} booking={commentBooking}
                                onClose={() => { setCommentBooking(null); load(); }} />
      <BookingDetailDialog ws={ws} booking={detailBooking}
                              spaces={spaces} members={members}
                              clients={clients} projects={projects}
                              canManage={canManage}
                              onClose={() => setDetailBooking(null)}
                              onChanged={load} />
    </div>
  );
}

// Feb 2026 · R268yyy · Booking comments — reuses the generic
// CommentThread component (entity_type=studio_booking) so admins get
// threaded replies, edits, deletes and @-mentions for free. Ticking
// "Visible to customer (emails them)" fires an email to the booking's
// customer_email via the shared messaging_service.
function BookingCommentsDialog({ ws, booking, onClose }) {
  if (!booking) return null;
  const refTag = booking.ref_no ? `#${booking.ref_no}` : "";
  const dateTag = booking.slot_start?.replace("T", " ").slice(0, 16) || "";
  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="sb-booking-comments-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-semibold">
            <MessageSquare className="w-4 h-4" /> Comments · {booking.code} {refTag}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {booking.customer_name || "—"} · {dateTag} · Tick <span className="font-semibold">Visible to customer</span> when you want the comment emailed to <span className="font-mono">{booking.customer_email || "—"}</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto -mx-2 px-2">
          <CommentThread
            entityType="studio_booking"
            entityId={booking.id}
            title="Booking discussion"
            emptyHint="Log internal notes or ping the customer with an update — everything shows up here."
            testid={`sb-booking-thread-${booking.id}`}
            renderCommentFooter={({ c, reload }) => (
              <RescheduleAcceptFooter comment={c} booking={booking} onDone={reload} />
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}




// R268uuu · Feb 2026 · UPI verification dialog. Shows the customer's
// submitted proof (screenshot / txn id) so the admin can eye-ball it
// Feb 2026 · R268yyy · Powerful Edit-Booking dialog. Mirrors the
// Task-detail experience — two columns on desktop (editable fields on
// the left, threaded comments on the right), collapsible sections,
// inline auto-save via PATCH /studio/bookings/{id}, one-click actions
// (Verify UPI · Release deposit · Cancel) and a read-only payment log.
export function BookingDetailDialog({ ws, booking, spaces, members, clients, projects, canManage, onClose, onChanged }) {
  const [row, setRow] = useState(booking);
  const [saving, setSaving] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [upiOpen, setUpiOpen] = useState(false);

  useEffect(() => { setRow(booking); }, [booking?.id]);
  if (!booking || !row) return null;

  const patch = async (fields) => {
    setSaving(true);
    try {
      await api.patch(`/workspaces/${ws}/studio/bookings/${row.id}`, fields);
      setRow((r) => ({ ...r, ...fields, updated_at: new Date().toISOString() }));
      onChanged?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const cancelBooking = async () => {
    try {
      const { data } = await api.post(`/workspaces/${ws}/studio/bookings/${row.id}/cancel`, { reason: "Admin cancellation" });
      toast.success(`Cancelled · refund ₹${data.refund_amount} (${data.refund_pct}%)`);
      setRow((r) => ({ ...r, status: "cancelled" }));
      setConfirmCancel(false);
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Cancel failed")); }
  };

  const dep = Number(row.quote?.damage_deposit || 0);
  const released = !!row.deposit_released_at;
  const spaceName = spaces.find((s) => s.id === row.space_id)?.name || (row.space_id ? row.space_id.slice(0, 8) : "—");
  const memberLabel = (uid) => {
    const m = members.find((x) => (x.user_id || x.id) === uid);
    return m?.name || m?.email || uid.slice(0, 6);
  };
  const clientName = clients.find((c) => c.id === row.client_id)?.name || "";
  const projectName = projects.find((p) => p.id === row.project_id)?.name || "";
  const isoLocal = (iso) => iso ? iso.slice(0, 16) : "";
  const durationLbl = (start, end) => {
    if (!start || !end) return "";
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (!(ms > 0)) return "";
    const h = Math.floor(ms / 3600000);
    const m = Math.round((ms % 3600000) / 60000);
    return `${h}h${m ? ` ${m}m` : ""}`;
  };
  const refTag = row.ref_no ? ` · #${row.ref_no}` : "";
  const pb = (row.upi_proof || row.txn_id)
    ? { label: "UPI QR", tone: "emerald" }
    : row.cashfree_order_id
      ? { label: "Cashfree", tone: "primary" }
      : { label: (row.source === "manual" || row.source === "admin") ? "Manual" : "—", tone: "muted" };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl rounded-none p-0 overflow-hidden" data-testid="sb-booking-detail-dialog">
        {/* Reserve room on the right for the Shadcn close (×) button
             so quick-action pills never sit under it. */}
        <div className="bg-primary/5 border-b border-border px-5 py-3 pr-14 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-0.5">
              Studio booking{refTag}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-base font-semibold">{row.code}</span>
              <span className={`text-[10px] px-1.5 py-0.5 border ${statusColor(row.status)}`}
                     data-testid="sb-booking-detail-status">
                {row.status}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 border border-border text-muted-foreground uppercase tracking-widest">
                Source: {row.source || "public"}
              </span>
              {row.is_off_hours && (
                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 uppercase">
                  Off-hours
                </span>
              )}
            </div>
            {saving && <div className="text-[10px] text-muted-foreground mt-1">Saving…</div>}
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-1.5">
              {row.status === "pending_verification" && (
                <Button size="sm" variant="outline" className="rounded-none h-7 text-emerald-700 border-emerald-300"
                         onClick={() => setUpiOpen(true)}
                         data-testid="sb-booking-detail-verify">
                  ✓ Verify UPI
                </Button>
              )}
              {dep > 0 && !released && (
                <Button size="sm" variant="outline" className="rounded-none h-7"
                         onClick={() => setDepositOpen(true)}
                         data-testid="sb-booking-detail-release">
                  <IndianRupee className="w-3 h-3 mr-1" /> Release deposit
                </Button>
              )}
              {row.status !== "cancelled" && (
                <Button size="sm" variant="outline" className="rounded-none h-7 text-rose-600 border-rose-300"
                         onClick={() => setConfirmCancel(true)}
                         data-testid="sb-booking-detail-cancel">
                  <XIcon className="w-3 h-3 mr-1" /> Cancel booking
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-[1fr_360px] max-h-[75vh]">
          <div className="p-5 space-y-5 overflow-y-auto">
            <BookingSection title="Status & schedule">
              <div className="grid grid-cols-2 gap-3">
                <BookingField label="Status">
                  <select value={row.status} onChange={(e) => patch({ status: e.target.value })}
                          disabled={!canManage || saving}
                          className="rounded-none h-8 w-full border border-input bg-transparent text-xs px-2"
                          data-testid="sb-booking-edit-status">
                    {["pending","pending_verification","confirmed","cancelled","completed"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </BookingField>
                <BookingField label="Space">
                  <div className="text-xs h-8 flex items-center">{row.is_offsite ? "🚚 Offsite" : spaceName}</div>
                </BookingField>
                <BookingField label="Slot start">
                  <Input type="datetime-local" value={isoLocal(row.slot_start)}
                         onChange={(e) => setRow((r) => ({ ...r, slot_start: e.target.value + ":00" }))}
                         onBlur={() => row.slot_start !== booking.slot_start && patch({ slot_start: row.slot_start })}
                         disabled={!canManage || saving}
                         className="rounded-none h-8 text-xs"
                         data-testid="sb-booking-edit-slot-start" />
                </BookingField>
                <BookingField label={`Slot end · ${durationLbl(row.slot_start, row.slot_end)}`}>
                  <Input type="datetime-local" value={isoLocal(row.slot_end)}
                         onChange={(e) => setRow((r) => ({ ...r, slot_end: e.target.value + ":00" }))}
                         onBlur={() => row.slot_end !== booking.slot_end && patch({ slot_end: row.slot_end })}
                         disabled={!canManage || saving}
                         className="rounded-none h-8 text-xs"
                         data-testid="sb-booking-edit-slot-end" />
                </BookingField>
              </div>
            </BookingSection>

            <BookingSection title="Customer">
              <div className="grid grid-cols-2 gap-3">
                <BookingField label="Name">
                  <Input value={row.customer_name || ""}
                         onChange={(e) => setRow((r) => ({ ...r, customer_name: e.target.value }))}
                         onBlur={() => row.customer_name !== booking.customer_name && patch({ customer_name: row.customer_name })}
                         disabled={!canManage || saving}
                         className="rounded-none h-8 text-xs"
                         data-testid="sb-booking-edit-customer-name" />
                </BookingField>
                <BookingField label="Email">
                  <Input type="email" value={row.customer_email || ""}
                         onChange={(e) => setRow((r) => ({ ...r, customer_email: e.target.value }))}
                         onBlur={() => row.customer_email !== booking.customer_email && patch({ customer_email: row.customer_email })}
                         disabled={!canManage || saving}
                         className="rounded-none h-8 text-xs"
                         data-testid="sb-booking-edit-customer-email" />
                </BookingField>
                <BookingField label="Phone">
                  <Input value={row.customer_phone || ""}
                         onChange={(e) => setRow((r) => ({ ...r, customer_phone: e.target.value }))}
                         onBlur={() => row.customer_phone !== booking.customer_phone && patch({ customer_phone: row.customer_phone })}
                         disabled={!canManage || saving}
                         className="rounded-none h-8 text-xs"
                         data-testid="sb-booking-edit-customer-phone" />
                </BookingField>
                <BookingField label="Booked on">
                  <div className="text-xs h-8 flex items-center text-muted-foreground">
                    {(row.created_at || "").replace("T", " ").slice(0, 16) || "—"}
                  </div>
                </BookingField>
              </div>
              <div className="flex gap-2 mt-2">
                {row.customer_email && (
                  <a href={`mailto:${row.customer_email}`}
                      className="text-[10px] text-muted-foreground hover:text-primary inline-flex items-center gap-1 border border-border px-2 py-1">
                    <Mail className="w-3 h-3" /> Email
                  </a>
                )}
                {row.customer_phone && (
                  <>
                    <a href={`tel:${row.customer_phone}`}
                        className="text-[10px] text-muted-foreground hover:text-primary inline-flex items-center gap-1 border border-border px-2 py-1">
                      <Phone className="w-3 h-3" /> Call
                    </a>
                    <a href={`https://wa.me/${(() => { const d = String(row.customer_phone).replace(/\D+/g,""); return d.length === 10 ? `91${d}` : d; })()}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-[10px] text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 px-2 py-1">
                      <MessageCircle className="w-3 h-3" /> WhatsApp
                    </a>
                  </>
                )}
              </div>
            </BookingSection>

            <BookingSection title="Assignments & links">
              <div className="grid grid-cols-2 gap-3">
                <BookingField label={`Assigned crew (${(row.assigned_user_ids || []).length})`}>
                  <div className="flex items-center gap-1.5 flex-wrap min-h-[32px]">
                    {(row.assigned_user_ids || []).length === 0
                      ? <span className="text-[10px] text-muted-foreground italic">Unassigned</span>
                      : (row.assigned_user_ids || []).slice(0, 3).map((uid) => (
                          <span key={uid} className="text-[10px] bg-primary/10 text-primary px-1 py-0.5 border border-primary/30">
                            {memberLabel(uid)}
                          </span>
                        ))}
                    {(row.assigned_user_ids || []).length > 3 && (
                      <span className="text-[10px] text-muted-foreground">+{(row.assigned_user_ids || []).length - 3}</span>
                    )}
                    {canManage && (
                      <button onClick={() => setAssignOpen(true)}
                              className="text-[10px] text-primary hover:underline"
                              data-testid="sb-booking-edit-assign">
                        {(row.assigned_user_ids || []).length ? "edit" : "assign"}
                      </button>
                    )}
                  </div>
                </BookingField>
                <BookingField label="Client / Project">
                  <div className="text-xs min-h-[32px] flex items-center gap-2 flex-wrap">
                    {clientName || projectName
                      ? <span>{clientName || ""}{clientName && projectName ? " · " : ""}{projectName || ""}</span>
                      : <span className="text-muted-foreground italic">Not linked</span>}
                    {canManage && (
                      <button onClick={() => setLinkOpen(true)}
                              className="text-[10px] text-primary hover:underline"
                              data-testid="sb-booking-edit-link">
                        {row.client_id || row.project_id ? "edit" : "link"}
                      </button>
                    )}
                  </div>
                </BookingField>
              </div>
            </BookingSection>

            <BookingSection title="Notes & shoot brief">
              <Textarea value={row.notes || ""}
                        onChange={(e) => setRow((r) => ({ ...r, notes: e.target.value }))}
                        onBlur={() => row.notes !== booking.notes && patch({ notes: row.notes })}
                        disabled={!canManage || saving}
                        className="rounded-none text-xs min-h-[80px]"
                        placeholder="Shoot brief, references, equipment prep notes…"
                        data-testid="sb-booking-edit-notes" />
              {row.is_offsite && (
                <div className="mt-2">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Location notes</Label>
                  <Textarea value={row.location_notes || ""}
                            onChange={(e) => setRow((r) => ({ ...r, location_notes: e.target.value }))}
                            onBlur={() => row.location_notes !== booking.location_notes && patch({ location_notes: row.location_notes })}
                            disabled={!canManage || saving}
                            className="rounded-none text-xs min-h-[60px]"
                            placeholder="Address, floor, parking, on-site contact…"
                            data-testid="sb-booking-edit-location-notes" />
                </div>
              )}
            </BookingSection>

            <BookingSection title="Payment & totals">
              <div className="border border-border">
                <BookingQuoteRow label="Method" value={
                  <span className={`text-[10px] px-1.5 py-0.5 border ${
                    pb.tone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                    : pb.tone === "primary" ? "bg-primary/10 text-primary border-primary/30"
                    : "bg-muted text-muted-foreground border-border"
                  }`}>
                    {pb.label}
                  </span>
                } />
                {row.txn_id && <BookingQuoteRow label="Txn ID" value={<span className="font-mono text-[11px]">{row.txn_id}</span>} />}
                {row.cashfree_order_id && <BookingQuoteRow label="Cashfree order" value={<span className="font-mono text-[11px]">{row.cashfree_order_id}</span>} />}
                {row.cashfree_payment_id && <BookingQuoteRow label="Cashfree payment" value={<span className="font-mono text-[11px]">{row.cashfree_payment_id}</span>} />}
                {row.promo_code && <BookingQuoteRow label="Promo" value={<span className="font-mono uppercase text-amber-700">{row.promo_code}</span>} />}
                {row.upi_proof?.screenshot_url && (
                  <BookingQuoteRow label="UPI screenshot" value={
                    <a href={row.upi_proof.screenshot_url} target="_blank" rel="noopener noreferrer"
                        className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">
                      <ImageIcon className="w-3 h-3" /> View proof
                    </a>
                  } />
                )}
                <BookingQuoteRow label="Subtotal" value={`₹${(row.quote?.subtotal || 0).toFixed(2)}`} />
                {(row.quote?.discount || 0) > 0 && <BookingQuoteRow label="Discount" value={`−₹${row.quote.discount.toFixed(2)}`} tone="text-emerald-700" />}
                {(row.quote?.tax || 0) > 0 && <BookingQuoteRow label="Tax" value={`₹${row.quote.tax.toFixed(2)}`} />}
                <BookingQuoteRow label="Total" bold value={`₹${(row.quote?.total || 0).toFixed(2)}`} />
                {dep > 0 && (
                  <BookingQuoteRow label="Damage deposit" value={
                    released
                      ? <span className="text-emerald-700">✓ Refunded ₹{(row.deposit_refund_amount || 0).toFixed(2)} on {row.deposit_released_at?.slice(0, 10)}</span>
                      : <span>₹{dep.toFixed(2)} <span className="text-[10px] text-muted-foreground">(held)</span></span>
                  } />
                )}
                {row.refund_amount > 0 && (
                  <BookingQuoteRow label="Refund on cancel" value={`₹${row.refund_amount.toFixed(2)} (${row.refund_pct}%)`} tone="text-emerald-700" />
                )}
              </div>
              {/* Feb 2026 · R280.4 · Payment ledger */}
              <BookingPaymentsLedger ws={ws} booking={row} onChanged={onChanged}
                                            canManage={canManage} />
            </BookingSection>
            {/* Oct 2026 · WhatsApp send log — last 20 attempts per
                booking so admins can see at a glance whether a
                confirmation / reminder / cancel template actually
                landed, and if not, exactly why Interakt rejected it. */}
            <BookingWaLog ws={ws} bookingId={row.id} />
          </div>

          <div className="border-l border-border bg-muted/20 overflow-y-auto flex flex-col">
            <div className="px-4 py-3 border-b border-border bg-background">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
                <MessageSquare className="w-3 h-3" /> Discussion
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                Tick <span className="font-semibold">Visible to customer</span> to email <span className="font-mono">{row.customer_email || "—"}</span>.
              </div>
            </div>
            <div className="p-3 flex-1">
              <CommentThread
                entityType="studio_booking"
                entityId={row.id}
                title=""
                emptyHint="Log internal notes or update the customer — everything lands here."
                testid={`sb-booking-detail-thread-${row.id}`}
                renderCommentFooter={({ c, reload }) => (
                  <RescheduleAcceptFooter comment={c} booking={row} onDone={reload} />
                )}
              />
            </div>
          </div>
        </div>

        {confirmCancel && (
          <Dialog open onOpenChange={(o) => !o && setConfirmCancel(false)}>
            <DialogContent className="max-w-md rounded-none">
              <DialogHeader>
                <DialogTitle>Cancel this booking?</DialogTitle>
                <DialogDescription className="text-xs">
                  The customer will be refunded based on your studio's cancellation policy. Linked crew tasks will also be cancelled.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" className="rounded-none" onClick={() => setConfirmCancel(false)}>Keep booking</Button>
                <Button className="rounded-none bg-rose-600 hover:bg-rose-700 text-white"
                         onClick={cancelBooking}
                         data-testid="sb-booking-detail-cancel-confirm">
                  Cancel booking
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        <ReleaseDepositDialog ws={ws} booking={depositOpen ? row : null}
                                onClose={() => setDepositOpen(false)}
                                onDone={() => { setDepositOpen(false); onChanged?.(); }} />
        <AssignCrewDialog ws={ws} booking={assignOpen ? row : null} members={members}
                            onClose={() => setAssignOpen(false)}
                            onDone={() => { setAssignOpen(false); onChanged?.(); }} />
        <LinkClientProjectDialog ws={ws} booking={linkOpen ? row : null} clients={clients}
                                   onClose={() => setLinkOpen(false)}
                                   onDone={() => { setLinkOpen(false); onChanged?.(); }} />
        <UpiVerifyDialog ws={ws} booking={upiOpen ? row : null}
                            onClose={() => setUpiOpen(false)}
                            onDone={() => { setUpiOpen(false); onChanged?.(); }} />
      </DialogContent>
    </Dialog>
  );
}

function BookingSection({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 pb-1 border-b border-border/60">
        {title}
      </div>
      {children}
    </div>
  );
}
function BookingField({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
function BookingQuoteRow({ label, value, bold, tone }) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5 border-b last:border-b-0 border-border/40 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={`${bold ? "font-semibold" : ""} ${tone || ""}`}>{value}</span>
    </div>
  );
}

/** Oct 2026 · WhatsApp send log for a booking. Reads the last 20
 *  attempts persisted by `_wa_log_insert` and renders them in a
 *  compact collapsible table. Rows include the Interakt rejection
 *  reason so admins can tell "template not approved" vs "no API key"
 *  vs "24-h window expired" without touching the server logs. */
function BookingWaLog({ ws, bookingId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendingId, setResendingId] = useState("");
  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/workspaces/${ws}/studio/bookings/${bookingId}/wa-log`);
      setRows(data?.rows || []);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load WhatsApp log"));
    } finally {
      setLoading(false);
    }
  };
  const resend = async (row) => {
    if (!row?.id) return;
    setResendingId(row.id);
    try {
      const { data } = await api.post(`/workspaces/${ws}/studio/wa-log/${row.id}/resend`);
      if (data?.ok) {
        toast.success(`WhatsApp resent to ${data.to_phone}`);
      } else {
        const r = data?.result || {};
        toast.error(`Resend did not send — ${r.reason || "unknown"}${r.detail ? ` · ${r.detail}` : ""}`);
      }
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Resend failed"));
    } finally {
      setResendingId("");
    }
  };
  useEffect(() => {
    if (open && rows.length === 0 && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const sentCount    = rows.filter((r) => r.status === "sent").length;
  const skippedCount = rows.filter((r) => r.status === "skipped").length;
  const failedCount  = rows.filter((r) => r.status && r.status !== "sent" && r.status !== "skipped").length;
  return (
    <div className="mt-5" data-testid="sb-booking-detail-wa-log">
      <button type="button"
              onClick={() => setOpen((v) => !v)}
              className="w-full text-left flex items-center justify-between pb-1 border-b border-border/60 hover:bg-muted/20 px-1"
              data-testid="sb-booking-detail-wa-log-toggle">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold inline-flex items-center gap-1.5">
          <MessageCircle className="w-3 h-3" /> WhatsApp log
          {rows.length > 0 && (
            <span className="text-[10px] text-muted-foreground normal-case tracking-normal font-normal">
              · {rows.length}
              {sentCount    > 0 && <span className="ml-1.5 text-emerald-700">✓ {sentCount}</span>}
              {failedCount  > 0 && <span className="ml-1.5 text-rose-700">✗ {failedCount}</span>}
              {skippedCount > 0 && <span className="ml-1.5 text-amber-700">· {skippedCount} skipped</span>}
            </span>
          )}
        </span>
        <span className="text-muted-foreground text-xs">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="mt-2 border border-border bg-background"
              data-testid="sb-booking-detail-wa-log-body">
          <div className="flex items-center justify-between px-2 py-1 border-b border-border bg-muted/30">
            <span className="text-[10px] text-muted-foreground">
              Shows the last 20 WhatsApp sends for this booking (newest first).
            </span>
            <Button type="button" size="sm" variant="ghost"
                     className="h-6 px-2 rounded-none text-[10px]"
                     onClick={load} disabled={loading}
                     data-testid="sb-booking-detail-wa-log-refresh">
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
          {error && <div className="px-3 py-2 text-xs text-rose-700 bg-rose-50">{error}</div>}
          {!loading && !error && rows.length === 0 && (
            <div className="px-3 py-6 text-xs text-muted-foreground text-center" data-testid="sb-booking-detail-wa-log-empty">
              No WhatsApp attempts recorded for this booking yet.
            </div>
          )}
          {rows.length > 0 && (
            <ul className="divide-y divide-border">
              {rows.map((r) => (
                <li key={r.id} className="px-3 py-2 text-xs"
                     data-testid={`sb-booking-detail-wa-log-row-${r.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <WaLogBadge status={r.status} />
                      <span className="font-mono text-[11px] text-foreground">
                        {r.kind}
                      </span>
                      {r.template_name && (
                        <span className="text-[10px] text-muted-foreground truncate">
                          · {r.template_name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground">
                        {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                      </span>
                      <Button type="button" size="sm" variant="outline"
                               className="h-6 px-2 rounded-none text-[10px] uppercase tracking-wider"
                               onClick={() => resend(r)}
                               disabled={resendingId === r.id || !r.to_phone || !r.template_name}
                               title={!r.to_phone ? "No recipient phone on this row"
                                      : !r.template_name ? "No template on this row"
                                      : "Resend this WhatsApp template"}
                               data-testid={`sb-booking-detail-wa-log-resend-${r.id}`}>
                        <Send className={`w-3 h-3 mr-1 ${resendingId === r.id ? "animate-pulse" : ""}`} />
                        {resendingId === r.id ? "Resending…" : "Resend"}
                      </Button>
                    </div>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap">
                    {r.to_phone && (
                      <span><span className="opacity-70">to</span> <span className="font-mono">{r.to_phone}</span></span>
                    )}
                    {r.provider_id && (
                      <span><span className="opacity-70">provider</span> <span className="font-mono">{r.provider_id}</span></span>
                    )}
                    {(r.reason || r.detail) && (
                      <span className={`${r.status === "sent" ? "" : "text-rose-700"} break-all`}>
                        · {r.reason}{r.detail ? ` — ${r.detail}` : ""}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function WaLogBadge({ status }) {
  const map = {
    sent:    { label: "SENT",    cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    skipped: { label: "SKIPPED", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    failed:  { label: "FAILED",  cls: "bg-rose-100 text-rose-800 border-rose-300" },
    exception: { label: "ERROR", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  };
  const { label, cls } = map[status] || { label: (status || "?").toUpperCase(), cls: "bg-muted text-muted-foreground border-border" };
  return (
    <span className={`text-[9px] px-1.5 py-0.5 border uppercase tracking-widest ${cls}`}>
      {label}
    </span>
  );
}


// against their bank/UPI app before confirming the booking.
function UpiVerifyDialog({ ws, booking, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("upi");
  const [txnId, setTxnId] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!booking) return;
    const paid = (booking.payments || []).filter((p) => (p.status || "success") === "success").reduce((s, p) => s + Number(p.amount || 0), 0);
    const bal = Math.max(0, Number(booking.quote?.total || 0) - paid);
    setAmount(bal ? String(Math.round(bal * 100) / 100) : "");
    setTxnId(booking.upi_proof?.txn_id || "");
    setNote("");
    setMethod("upi");
  }, [booking?.id]);
  if (!booking) return null;
  const proof = booking.upi_proof || {};
  const shotUrl = proof.screenshot_url
    ? (proof.screenshot_url.startsWith("http")
        ? proof.screenshot_url
        : `${process.env.REACT_APP_BACKEND_URL}${proof.screenshot_url}`)
    : "";
  const confirm = async () => {
    setBusy(true);
    try {
      const amt = Number(amount);
      const body = amt > 0
        ? { amount: amt, method, txn_id: txnId.trim(), note: note.trim() }
        : {};
      const { data } = await api.post(`/workspaces/${ws}/studio/bookings/${booking.id}/confirm-upi`, body);
      const bal = Number(data?.balance || 0);
      const excess = Number(data?.excess || 0);
      if (excess > 0) toast.success(`Confirmed with ₹${excess.toFixed(2)} excess held on account`);
      else if (bal > 0) toast.success(`Confirmed · balance ₹${bal.toFixed(2)} still outstanding`);
      else toast.success("Booking confirmed and paid in full");
      onClose(); onDone && onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Confirm failed")); }
    finally { setBusy(false); }
  };
  const refLabel = booking.ref_no ? `#${booking.ref_no}` : booking.code;
  const total = Number(booking.quote?.total || 0);
  const paid = (booking.payments || []).filter((p) => (p.status || "success") === "success").reduce((s, p) => s + Number(p.amount || 0), 0);
  const currentBalance = Math.max(0, total - paid);
  return (
    <Dialog open={!!booking} onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-none" data-testid="sb-upi-verify-dialog">
        <DialogHeader>
          <DialogTitle>Verify UPI payment · {refLabel}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-xs">
            <b>{booking.customer_name}</b> · {booking.customer_email} · {booking.customer_phone}
          </div>
          <div className="text-xs">
            Total: <b className="font-mono">₹{total.toFixed(2)}</b>
            {paid > 0 && <> · Paid so far: <b className="font-mono">₹{paid.toFixed(2)}</b> · Balance: <b className="font-mono">₹{currentBalance.toFixed(2)}</b></>}
          </div>
          <div className="border border-border p-3 bg-muted/20 space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Customer submitted
            </div>
            {proof.txn_id ? (
              <div className="text-xs">Txn ID: <span className="font-mono font-semibold" data-testid="sb-upi-verify-txn">{proof.txn_id}</span></div>
            ) : (
              <div className="text-[11px] text-muted-foreground italic">No txn ID provided</div>
            )}
            {shotUrl ? (
              <a href={shotUrl} target="_blank" rel="noopener noreferrer" data-testid="sb-upi-verify-shot-link">
                <img src={shotUrl} alt="proof"
                      className="w-full max-h-96 object-contain border border-border"
                      data-testid="sb-upi-verify-shot" />
              </a>
            ) : (
              <div className="text-[11px] text-muted-foreground italic">No screenshot uploaded</div>
            )}
            {proof.submitted_at && (
              <div className="text-[10px] text-muted-foreground">
                Submitted {proof.submitted_at.replace("T", " ").slice(0, 16)}
              </div>
            )}
          </div>
          {/* Feb 2026 · R280.4 · Record the exact amount received so the
              payment ledger + balance stay accurate. Blank amount → just
              flips status without a ledger entry (legacy behavior). */}
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest">Amount received (₹)</Label>
              <Input type="number" step="0.01" value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder={currentBalance.toFixed(2)}
                        data-testid="sb-upi-verify-amount"
                        className="rounded-none h-8 text-xs font-mono" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Method</Label>
              <select value={method} onChange={(e) => setMethod(e.target.value)}
                         data-testid="sb-upi-verify-method"
                         className="w-full h-8 text-xs border border-border rounded-none bg-white px-2">
                <option value="upi">UPI</option>
                <option value="cash">Cash</option>
                <option value="neft">NEFT</option>
                <option value="card">Card</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Bank / Txn reference (optional)</Label>
            <Input value={txnId} onChange={(e) => setTxnId(e.target.value)}
                      placeholder="UPI/Bank ref shown in your statement"
                      data-testid="sb-upi-verify-txn-input"
                      className="rounded-none h-8 text-xs font-mono" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder='e.g. "Advance for shoot"'
                      data-testid="sb-upi-verify-note"
                      className="rounded-none h-8 text-xs" />
          </div>
          <div className="text-[11px] text-amber-700 bg-amber-50 border-l-2 border-amber-500 p-2">
            {Number(amount) > 0 && Number(amount) < currentBalance
              ? `This is a partial payment — the ${(currentBalance - Number(amount)).toFixed(2)} balance stays outstanding.`
              : Number(amount) > currentBalance
                ? `Amount exceeds the balance by ₹${(Number(amount) - currentBalance).toFixed(2)} — the excess will be tracked as credit on account.`
                : "Confirm only after you've verified this txn in your bank / UPI app."}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-none">Close</Button>
          <Button onClick={confirm} disabled={busy}
                    data-testid="sb-upi-verify-confirm"
                    className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white">
            {busy ? "Confirming…" : "✓ Record & confirm"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReleaseDepositDialog({ ws, booking, onClose, onDone }) {
  const [deductions, setDeductions] = useState([]);
  const [notes, setNotes] = useState("");
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDeductions([]); setNotes(""); setForce(false); }, [booking?.id]);
  if (!booking) return null;
  const dep = Number(booking.quote?.damage_deposit || 0);
  const totalDed = deductions.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const refund = Math.max(0, dep - totalDed);
  const submit = async () => {
    setBusy(true);
    try {
      const clean = deductions.filter((d) => d.reason && Number(d.amount) > 0)
        .map((d) => ({ reason: d.reason, amount: Number(d.amount) }));
      const { data } = await api.post(`/workspaces/${ws}/studio/bookings/${booking.id}/release-deposit`,
        { deductions: clean, notes, force });
      toast.success(`Refund of ₹${data.refund_amount.toFixed(2)} released`);
      onDone && await onDone();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Release failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="sb-release-deposit-dialog">
        <DialogHeader><DialogTitle>Release damage deposit · {booking.code}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="border border-border p-2"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Deposit</div><div className="font-mono">₹{dep.toFixed(2)}</div></div>
            <div className="border border-border p-2"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Deducted</div><div className="font-mono text-rose-700">−₹{totalDed.toFixed(2)}</div></div>
            <div className="border border-primary p-2 bg-primary/5"><div className="text-[10px] uppercase tracking-widest text-primary font-semibold">Refund</div><div className="font-mono font-bold">₹{refund.toFixed(2)}</div></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Deductions (damage, cleaning, missing items…)</Label>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                      onClick={() => setDeductions([...deductions, { reason: "", amount: 0 }])}
                      data-testid="sb-deduction-add">
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-1">
              {deductions.map((d, i) => (
                <div key={i} className="flex gap-1 items-center">
                  <Input value={d.reason} onChange={(e) => setDeductions(deductions.map((x, j) => j === i ? { ...x, reason: e.target.value } : x))}
                          placeholder="e.g. Scratched lens" className="rounded-none h-8 flex-1" data-testid={`sb-deduction-reason-${i}`} />
                  <Input type="number" value={d.amount} onChange={(e) => setDeductions(deductions.map((x, j) => j === i ? { ...x, amount: Number(e.target.value) } : x))}
                          placeholder="₹" className="rounded-none h-8 w-24" data-testid={`sb-deduction-amount-${i}`} />
                  <button onClick={() => setDeductions(deductions.filter((_, j) => j !== i))} className="text-rose-600 hover:text-rose-800">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {deductions.length === 0 && <div className="text-[11px] italic text-muted-foreground">No deductions — full deposit will be refunded.</div>}
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes to customer (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-none text-xs" data-testid="sb-release-notes" />
          </div>
          <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="sb-release-force" />
            Force release even if some equipment is still checked out
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="sb-release-submit">
            Release ₹{refund.toFixed(2)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Feb 2026 · Multi-select crew assignment for a booking.
function AssignCrewDialog({ ws, booking, members, onClose, onDone }) {
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPicked(booking?.assigned_user_ids || []); }, [booking?.id]);
  if (!booking) return null;
  const toggle = (uid) => setPicked((s) => s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/workspaces/${ws}/studio/bookings/${booking.id}/crew`, { user_ids: picked });
      toast.success(picked.length ? `Assigned ${picked.length} crew` : "Crew cleared");
      onDone && await onDone();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Assign failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="sb-assign-crew-dialog">
        <DialogHeader>
          <DialogTitle>Assign crew · {booking.code}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground mb-2">
          {booking.customer_name} · {booking.slot_start?.replace("T", " ").slice(0, 16)}
        </div>
        <div className="max-h-72 overflow-y-auto border border-border">
          {members.length === 0 && (
            <div className="text-xs italic text-muted-foreground p-3">No workspace members found.</div>
          )}
          {members.map((m) => {
            const uid = m.user_id || m.id;
            const on = picked.includes(uid);
            return (
              <label key={uid} className="flex items-center gap-2 px-2 py-1.5 border-b border-border cursor-pointer hover:bg-muted/40"
                     data-testid={`sb-assign-crew-row-${uid}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(uid)} />
                <span className="text-sm">{m.name || m.email || uid.slice(0, 8)}</span>
                {m.email && m.name && <span className="text-[10px] text-muted-foreground ml-auto">{m.email}</span>}
              </label>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="sb-assign-crew-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Feb 2026 · Link an existing booking to a client + project.
function LinkClientProjectDialog({ ws, booking, clients, onClose, onDone }) {
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setClientId(booking?.client_id || "");
    setProjectId(booking?.project_id || "");
  }, [booking?.id]);
  useEffect(() => {
    if (!clientId) { setProjects([]); return; }
    api.get(`/clients/${clientId}/projects`).then((r) => setProjects(r.data || []))
       .catch(() => setProjects([]));
  }, [clientId]);
  if (!booking) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${ws}/studio/bookings/${booking.id}`, {
        client_id: clientId || null,
        project_id: projectId || null,
      });
      toast.success("Booking linked");
      onDone && await onDone();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Link failed")); }
    finally { setBusy(false); }
  };
  const clear = () => { setClientId(""); setProjectId(""); };
  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="sb-link-cp-dialog">
        <DialogHeader>
          <DialogTitle>Link client &amp; project · {booking.code}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground mb-2">
          {booking.customer_name} · {booking.slot_start?.replace("T", " ").slice(0, 16)}
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Client</Label>
            <select value={clientId}
                    onChange={(e) => { setClientId(e.target.value); setProjectId(""); }}
                    className="w-full h-9 border border-border bg-background text-xs rounded-none px-2"
                    data-testid="sb-link-cp-client">
              <option value="">— None —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Project</Label>
            <select value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    disabled={!clientId}
                    className="w-full h-9 border border-border bg-background text-xs rounded-none px-2 disabled:opacity-50"
                    data-testid="sb-link-cp-project">
              <option value="">{clientId ? "— None —" : "Pick client first"}</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={clear} disabled={busy}
                  data-testid="sb-link-cp-clear">Clear</Button>
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="sb-link-cp-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RescheduleAcceptFooter({ comment, booking, onDone }) {
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  const { can } = useWorkspacePerms();
  const meta = comment?.meta || {};
  if (meta.kind !== "reschedule_request") return null;
  if (!can("studio.bookings.manage") && !can("workspace.admin")) return null;
  if (meta.accepted_at) {
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] uppercase tracking-widest px-2 py-1"
              data-testid={`sb-reschedule-accepted-${comment.id}`}>
        Accepted · {new Date(meta.accepted_at).toLocaleString()}
      </div>
    );
  }
  const nice = (s) => (s || "").replace("T", " ").slice(0, 16);
  const accept = async () => {
    setBusy(true); setConflict(null);
    try {
      const { data } = await api.post(
        `/workspaces/${booking.workspace_id}/studio/bookings/${booking.id}/accept-reschedule`,
        { comment_id: comment.id }
      );
      if (data.ok === false) {
        setConflict(data);
        toast.error("That slot has a conflict — patch manually from the booking detail.");
      } else {
        toast.success(`Booking moved to ${nice(data.slot_start)}`);
        if (onDone) onDone();
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Accept failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 flex flex-col gap-1 text-[11px]"
             data-testid={`sb-reschedule-footer-${comment.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-muted-foreground text-[10px]">
          Preferred slot: <span className="font-mono font-semibold text-foreground">{nice(meta.preferred_start)}{meta.preferred_end ? ` → ${nice(meta.preferred_end)}` : ""}</span>
        </div>
        <Button size="sm" onClick={accept} disabled={busy}
                data-testid={`sb-reschedule-accept-${comment.id}`}
                className="rounded-none h-6 text-[10px] uppercase tracking-widest">
          <CalendarDays className="w-3 h-3 mr-1" />
          {busy ? "Moving…" : "Accept & move slot"}
        </Button>
      </div>
      {conflict && conflict.available === false && (
        <div className="border border-rose-200 bg-rose-50 text-rose-700 p-2 text-[10px]">
          <div className="font-semibold uppercase tracking-widest">Conflict — can't auto-move</div>
          <div className="mt-1">Reason: {conflict.reason || "space/equipment busy"}</div>
          {(conflict.conflicts?.space || []).length > 0 && (
            <div>Overlaps with {conflict.conflicts.space.length} booking(s).</div>
          )}
          <div className="mt-1 italic">Open the booking's Edit dialog to pick an alternate slot.</div>
        </div>
      )}
    </div>
  );
}



// ─── Feb 2026 · R280.4 · Payments ledger for a booking ───────────────
// Renders inside the Booking Detail dialog's "Payment & totals"
// section. Shows every recorded payment (with method/txn_id/note),
// the running paid + balance + excess totals, and lets admins with
// `studio.bookings.manage` add new payments or void mistakes.
function BookingPaymentsLedger({ ws, booking, onChanged, canManage }) {
  const [open, setOpen] = useState(false);
  const rows = (booking?.payments || []).filter((p) => (p.status || "success") !== "void");
  const voided = (booking?.payments || []).filter((p) => (p.status || "success") === "void");
  const total = Number(booking?.quote?.total || 0);
  const paid = rows.reduce((s, p) => s + Number(p.amount || 0), 0);
  const balance = total - paid;
  const excess = Math.max(0, paid - total);
  const bal_tone = balance > 0 ? "text-rose-700" : balance < 0 ? "text-emerald-700" : "text-muted-foreground";
  return (
    <div className="mt-3 border border-border" data-testid={`sb-booking-payments-${booking.id}`}>
      <div className="px-3 py-2 flex items-center justify-between bg-muted/30 border-b border-border">
        <div className="text-[10px] uppercase tracking-widest font-semibold">Payment ledger</div>
        {canManage && (
          <Button size="sm" onClick={() => setOpen(true)}
                    data-testid={`sb-booking-payments-add-${booking.id}`}
                    className="rounded-none h-6 text-[10px] uppercase tracking-widest">
            <Plus className="w-3 h-3 mr-1" /> Record payment
          </Button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="p-3 text-[11px] text-muted-foreground italic">No payments recorded yet.</div>
      ) : (
        <div className="divide-y divide-border">
          {rows.map((p) => (
            <div key={p.id} className="flex items-start gap-3 px-3 py-2 text-xs" data-testid={`sb-booking-payment-row-${p.id}`}>
              <div className="font-mono font-semibold w-24 shrink-0 text-right">
                {p.amount < 0 ? <span className="text-rose-700">−₹{Math.abs(p.amount).toFixed(2)}</span> : <span className="text-emerald-700">₹{Number(p.amount).toFixed(2)}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {(p.method || "cash").toUpperCase()}{p.txn_id ? ` · ${p.txn_id}` : ""}{p.source ? ` · ${p.source}` : ""}
                </div>
                {p.note && <div className="text-[11px] mt-0.5">{p.note}</div>}
                <div className="text-[10px] text-muted-foreground mt-0.5">{p.recorded_at?.replace("T", " ").slice(0, 16)}</div>
              </div>
              {canManage && (
                <>
                  <button onClick={async () => {
                    if (!booking.customer_email) { toast.error("Booking has no customer email on file"); return; }
                    try {
                      const { data } = await api.post(`/workspaces/${ws}/studio/bookings/${booking.id}/payments/${p.id}/send-receipt`);
                      toast.success(`Receipt PDF emailed to ${data.sent_to}`);
                      onChanged && onChanged();
                    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
                  }} className="text-muted-foreground hover:text-primary"
                        data-testid={`sb-booking-payment-receipt-${p.id}`}
                        title="Email a branded PDF receipt to the customer">
                    <Send className="w-3 h-3" />
                  </button>
                  <button onClick={async () => {
                    if (!window.confirm("Void this payment? The audit trail is preserved.")) return;
                    try {
                      await api.delete(`/workspaces/${ws}/studio/bookings/${booking.id}/payments/${p.id}`);
                      toast.success("Payment voided");
                      onChanged && onChanged();
                    } catch (e) { toast.error(apiErrorMessage(e, "Void failed")); }
                  }} className="text-muted-foreground hover:text-rose-600"
                      data-testid={`sb-booking-payment-void-${p.id}`}
                      title="Void this payment">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="px-3 py-2 bg-muted/20 border-t border-border grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Paid</div>
          <div className="font-mono font-semibold">₹{paid.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div>
          <div className="font-mono">₹{total.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{balance < 0 ? "Credit" : "Balance"}</div>
          <div className={`font-mono font-semibold ${bal_tone}`} data-testid={`sb-booking-payments-balance-${booking.id}`}>
            {balance === 0 ? "Paid" : balance < 0 ? `+₹${Math.abs(balance).toFixed(2)}` : `₹${balance.toFixed(2)}`}
          </div>
        </div>
      </div>
      {voided.length > 0 && (
        <details className="border-t border-border px-3 py-1">
          <summary className="text-[10px] text-muted-foreground uppercase tracking-widest cursor-pointer">
            Voided payments ({voided.length})
          </summary>
          <div className="text-[11px] text-muted-foreground pb-2 space-y-1">
            {voided.map((p) => (
              <div key={p.id} className="flex items-center gap-2 line-through">
                <span className="font-mono">₹{Number(p.amount).toFixed(2)}</span>
                <span>· {(p.method || "cash").toUpperCase()}</span>
                <span>· voided {p.voided_at?.slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
      {excess > 0 && (
        <div className="px-3 py-1.5 bg-emerald-50 border-t border-emerald-200 text-[10px] text-emerald-800">
          Customer has paid ₹{excess.toFixed(2)} in excess — track as credit on their next booking.
        </div>
      )}
      <RecordPaymentDialog ws={ws} booking={booking} open={open} onClose={() => setOpen(false)} onDone={onChanged} />
    </div>
  );
}


function RecordPaymentDialog({ ws, booking, open, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [txnId, setTxnId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const paid = (booking?.payments || [])
      .filter((p) => (p.status || "success") === "success")
      .reduce((s, p) => s + Number(p.amount || 0), 0);
    const bal = Math.max(0, Number(booking?.quote?.total || 0) - paid);
    setAmount(bal ? String(Math.round(bal * 100) / 100) : "");
    setMethod("cash"); setTxnId(""); setNote("");
  }, [open, booking?.id]);
  if (!booking) return null;
  const submit = async () => {
    const amt = Number(amount);
    if (!amt) { toast.error("Enter a non-zero amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${ws}/studio/bookings/${booking.id}/record-payment`,
        { amount: amt, method, txn_id: txnId.trim(), note: note.trim() });
      const bal = Number(data.balance || 0);
      if (bal < 0) toast.success(`Recorded — ₹${Math.abs(bal).toFixed(2)} credit on account`);
      else if (bal > 0) toast.success(`Recorded — ₹${bal.toFixed(2)} still outstanding`);
      else toast.success("Recorded — booking paid in full");
      onClose(); onDone && onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Record failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-none" data-testid="sb-record-payment-dialog">
        <DialogHeader><DialogTitle>Record payment · {booking.ref_no ? `#${booking.ref_no}` : booking.code}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-xs">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest">Amount (₹)</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                     data-testid="sb-record-payment-amount"
                     className="rounded-none h-8 text-xs font-mono" />
              <div className="text-[10px] text-muted-foreground mt-0.5">Use a negative amount to log a refund.</div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Method</Label>
              <select value={method} onChange={(e) => setMethod(e.target.value)}
                      data-testid="sb-record-payment-method"
                      className="w-full h-8 text-xs border border-border rounded-none bg-white px-2">
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="neft">NEFT</option>
                <option value="card">Card</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Bank / Txn reference</Label>
            <Input value={txnId} onChange={(e) => setTxnId(e.target.value)}
                   data-testid="sb-record-payment-txn"
                   className="rounded-none h-8 text-xs font-mono" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                   data-testid="sb-record-payment-note"
                   className="rounded-none h-8 text-xs" placeholder="e.g. Balance settled at studio" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-none">Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  data-testid="sb-record-payment-submit"
                  className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white">
            {busy ? "Recording…" : "Record payment"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
